<template>
    <v-container :fluid="true" class="max-height">
        <v-row>
            <v-col class="px-5 d-flex justify-space-between align-center" cols="12">
                <div>
                    <h1 class="text-left text-h5 font-weight-bold">Payment Methods</h1>
                    <p class="text-body-2 text-medium-emphasis text-left mb-0">Manage your saved cards for subscriptions</p>
                </div>
                <v-btn color="primary" prepend-icon="mdi-credit-card-plus" @click="showCardDialog">Add Card</v-btn>
            </v-col>
        </v-row>

        <v-row>
            <v-col cols="12">
                <v-card variant="outlined" rounded="lg">
                    <v-table hover>
                        <thead>
                        <tr>
                            <th>Card Holder</th>
                            <th>Brand</th>
                            <th>Card Number</th>
                            <th>Default</th>
                            <th class="text-center" style="width: 100px">Actions</th>
                        </tr>
                        </thead>
                        <tbody>
                        <template v-if="loading">
                            <tr>
                                <td colspan="5" class="text-center pa-8">
                                    <v-progress-circular color="primary" indeterminate size="32"/>
                                    <p class="text-body-2 text-medium-emphasis mt-3 mb-0">Loading payment methods...</p>
                                </td>
                            </tr>
                        </template>
                        <template v-else-if="cards.length">
                            <tr v-for="card in cards" :key="card.id" class="text-left">
                                <td class="font-weight-medium">{{ card.card_holder_name }}</td>
                                <td>
                                    <v-chip size="small" variant="tonal" :color="cardBrandColor(card.brand)">
                                        <v-icon start size="14">{{ cardBrandIcon(card.brand) }}</v-icon>
                                        {{ strOperations().capitalize(card.brand) }}
                                    </v-chip>
                                </td>
                                <td class="text-medium-emphasis">**** {{ card.last_four }}</td>
                                <td>
                                    <v-chip v-if="card.default" size="small" color="success" variant="tonal">
                                        <v-icon start size="14">mdi-check-circle</v-icon>
                                        Default
                                    </v-chip>
                                </td>
                                <td class="text-center">
                                    <template v-if="!card.default">
                                        <v-btn icon="mdi-check" size="small" variant="text" color="success"
                                               title="Set as default" @click="setPmDefault(card)"/>
                                        <v-btn icon="mdi-delete-outline" size="small" variant="text" color="error"
                                               title="Delete card" @click="deletePm(card)"/>
                                    </template>
                                </td>
                            </tr>
                        </template>
                        <template v-else>
                            <tr>
                                <td colspan="5" class="text-center pa-10">
                                    <v-icon size="48" color="grey-lighten-1" class="mb-3">mdi-credit-card-off-outline</v-icon>
                                    <p class="text-body-1 text-medium-emphasis mb-1">No payment methods</p>
                                    <p class="text-body-2 text-medium-emphasis mb-3">
                                        Add a card to manage your subscriptions
                                    </p>
                                    <v-btn color="primary" prepend-icon="mdi-credit-card-plus" size="small"
                                           @click="showCardDialog">
                                        Add Card
                                    </v-btn>
                                </td>
                            </tr>
                        </template>
                        </tbody>
                    </v-table>
                </v-card>
            </v-col>
        </v-row>
    </v-container>

    <v-dialog v-model="dialogSwitch" persistent scrollable width="500">
        <v-form class="mb-3" validate-on="input lazy" @submit.prevent="submitCard">
            <v-card rounded="lg">
                <v-card-title class="d-flex justify-space-between align-center pa-4">
                    <span class="text-h6">Add Payment Method</span>
                    <v-btn icon="mdi-close" size="small" variant="text" @click="closeCardDialog"/>
                </v-card-title>
                <v-divider/>

                <v-container>
                    <v-alert v-if="errors.length" class="mb-3 bg-error" closable close-label="Close Alert"
                             density="compact" variant="tonal">
                        <p v-for="error in errors">{{ error }}</p>
                    </v-alert>
                    <v-row>
                        <v-col cols="12">
                            <v-text-field v-model="formData['name']" :rules="[vRules.required]" density="compact"
                                          label="Name on Card" variant="plain"/>
                            <div ref="cardElement" class="pa-3"></div>
                        </v-col>
                    </v-row>
                    <div class="trust-section pa-3 rounded-lg mt-1">
                        <div class="d-flex align-center mb-1">
                            <v-icon size="16" color="blue-darken-2" class="mr-2">mdi-shield-check</v-icon>
                            <span class="text-caption font-weight-medium" style="color: #1565C0">
                                Your card details are sent directly to Stripe and never stored on our servers
                            </span>
                        </div>
                        <div class="d-flex align-center mb-1 ml-6">
                            <v-icon size="12" color="grey-darken-1" class="mr-2">mdi-lock</v-icon>
                            <span style="font-size: 11px" class="text-grey-darken-1">256-bit SSL encryption - PCI DSS Level 1 certified</span>
                        </div>
                        <div class="d-flex align-center justify-center mt-2" style="gap: 6px">
                            <span style="font-size: 11px; color: #9E9E9E">Powered by</span>
                            <svg xmlns="http://www.w3.org/2000/svg" width="40" height="17" viewBox="0 0 60 25" fill="#635BFF">
                                <path d="M5 10.2c0-.7.6-1 1.5-1 1.3 0 3 .4 4.3 1.1V6.7c-1.4-.6-2.9-.8-4.3-.8C3.2 5.9.8 7.8.8 10.4c0 4.1 5.6 3.4 5.6 5.2 0 .9-.8 1.1-1.8 1.1-1.6 0-3.6-.6-5.2-1.5v3.6c1.8.8 3.5 1.1 5.2 1.1 3.4 0 5.8-1.7 5.8-4.4-.1-4.3-5.6-3.6-5.6-5.3zM14.4 3.8l-4 .9v3.7l4-.9V3.8zM10.4 9.6h4v10.2h-4V9.6zM19.2 9.6l-.3-1h-3.5v10.2h4v-6.9c.9-1.2 2.5-1 3-.8V7.6c-.5-.2-2.5-.6-3.2 2zM24.1 5.9l-4 .8v9.3c0 1.7 1.3 3 3 3 1 0 1.7-.2 2.1-.4v-3.1c-.4.1-2.2.6-2.2-1V12h2.2V9.6h-2.2V5.9zM32.8 12.5c0-.5.4-.7.9-.7.8 0 1.8.3 2.6.7V9.7c-.9-.4-1.7-.5-2.6-.5-2.1 0-3.6 1.1-3.6 3 0 2.9 4 2.5 4 3.7 0 .6-.5.8-1.2.8-1 0-2.3-.4-3.3-1v2.7c1.1.5 2.3.7 3.3.7 2.2 0 3.7-1.1 3.7-3 0-3.2-4-2.6-4-3.8l.2.2z"/>
                            </svg>
                        </div>
                        <div class="d-flex justify-center mt-2" style="gap: 8px">
                            <v-icon size="20" color="blue-darken-3" title="Visa">mdi-credit-card-outline</v-icon>
                            <v-icon size="20" color="deep-orange" title="Mastercard">mdi-credit-card-outline</v-icon>
                            <v-icon size="20" color="blue" title="Amex">mdi-credit-card-outline</v-icon>
                            <v-icon size="20" color="orange-darken-1" title="Discover">mdi-credit-card-outline</v-icon>
                        </div>
                    </div>
                </v-container>

                <v-card-actions class="px-4 pb-4 d-flex justify-end">
                    <v-btn variant="text" @click="closeCardDialog">Cancel</v-btn>
                    <v-btn :disabled="!formData.valid" :loading="loading" color="primary" type="submit">
                        Add Payment Method
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-form>
    </v-dialog>
</template>

<script setup>
import {send} from "@/composables/apiClient";
import {loadStripe} from '@stripe/stripe-js';
import {strOperations} from "@/composables/globals";
import {onBeforeUnmount, onMounted, reactive, ref, nextTick} from "vue";
import {useSnackbarStore} from "@/stores/snackBarStore";

const cardBrandIcon = (brand) => {
    const icons = {visa: 'mdi-credit-card', mastercard: 'mdi-credit-card', amex: 'mdi-credit-card', discover: 'mdi-credit-card'};
    return icons[brand?.toLowerCase()] || 'mdi-credit-card-outline';
};
const cardBrandColor = (brand) => {
    const colors = {visa: 'blue-darken-2', mastercard: 'deep-orange', amex: 'blue', discover: 'orange-darken-1'};
    return colors[brand?.toLowerCase()] || 'grey';
};
const cardElement = ref();
const cards = ref([]);
const errors = ref([]);
const card = ref(null);
const stripe = ref(null);
const loading = ref(true);
const elements = ref(null);
const setupIntent = ref(null);
const dialogSwitch = ref(false);
const {showSnackbar} = useSnackbarStore();
const formData = reactive({
    name: null,
    valid: false,
    cardValid: false,
});
const style = {
    base: {
        fontSize: '16px',
        fontFamily: "Avenir, Helvetica, Arial, sans-serif",
        '::placeholder': {
            color: '#96999C'
        }
    },
    invalid: {
        color: '#F10505',
        iconColor: '#F10505'
    }
};

onMounted(async () => {
    fetchPMs();
    stripe.value = await loadStripe(process.env.VUE_APP_STRIPE_KEY);
});
const fetchPMs = () => {
    send('get', `payments/methods`)
        .then((response) => {
            cards.value = response.data.data;
            loading.value = false;
        })
        .catch((error) => {
            loading.value = false;
            console.log(error)
            showSnackbar('Payment Methods could not be loaded', 'error')
        })
}

// Tear the Stripe card Element down before letting go of it.
//
// card.value = null only dropped OUR reference. The Element is an iframe Stripe
// mounted into the page, and it outlives the reference: dropping it leaves the
// iframe and its listeners attached. Both dialog close buttons did not even do
// that much - they flipped dialogSwitch and nothing else - so every open/close
// cycle called elements.create('card') again and left the previous Element
// mounted. Open the dialog five times and there are five live card iframes,
// each still holding whatever was typed into it.
//
// destroy() is the documented teardown and it is what makes the unmount
// complete. It throws if the Element is already destroyed, so the guard is not
// decoration - closeCardDialog and the submit path can both reach it.
const destroyCard = () => {
    try {
        card.value?.destroy();
    } catch (e) {
        // Already destroyed, or never mounted. Nothing to release either way.
    }
    card.value = null;
    elements.value = null;
};

// Closing the dialog has to release the Element too, not just hide it.
const closeCardDialog = () => {
    destroyCard();
    dialogSwitch.value = false;
};

// And leaving the page while the dialog is open is a close as well - the
// component goes, the iframe would not.
onBeforeUnmount(destroyCard);

const showCardDialog = async () => {
    loading.value = true;
    dialogSwitch.value = true;

    // Whatever the last dialog left behind goes first. The close buttons release
    // the Element now, but this is the one line that holds regardless of how the
    // dialog was dismissed, so re-opening can never stack a second live card
    // iframe on top of a first.
    destroyCard();

    elements.value = stripe.value.elements();
    card.value = elements.value.create('card', {hidePostalCode: true, style: style});
    card.value.on('change', function (event) {
        errors.value = [];
        if (event.error) {
            errors.value.push(event.error.message);
        } else if (event.complete) {
            formData['valid'] = true;
            if (!formData['name']) {
                errors.value.push("Card holder name is required");
            }
        }
    });

    await nextTick();
    if (card.value) {
        card.value.mount(cardElement.value);
    } else {
        console.log("Card Element not loaded")
    }
    loading.value = false;
}

const createSetupIntent = async () => {
    try {
        const response = await send('post', `payments/create-setup-intent`);
        return response.data.data;
    } catch (error) {
        console.log(error);
        showSnackbar('Setup Intent could not be created', 'error');
        throw error;
    }
}

const submitCard = async () => {
    errors.value = [];

    if (!formData['name']) {
        errors.value.push("Card holder name is required");
        return false;
    }

    try {
        loading.value = true;

        if (!setupIntent.value) {
            setupIntent.value = await createSetupIntent();
        }

        if (setupIntent.value) {
            await verifyCard();
        }
    } catch (e) {
        errors.value.push(e.message);
        return false;
    } finally {
        loading.value = false;
    }
};

const verifyCard = async () => {
    if (setupIntent.value) {
        const {setupIntent: confirmedIntent, error} = await stripe.value.confirmCardSetup(
            setupIntent.value.client_secret, {
                payment_method: {
                    card: card.value,
                    billing_details: {name: formData['name']}
                }
            });
        if (error) {
            errors.value.push(error.message);
            return false;
        } else {
            await savePaymentMethod(confirmedIntent.payment_method);
        }
    }
}
const savePaymentMethod = async (payment_method) => {
    try {
        const response = await send('post', `payments/create-method`, {
            payment_method: payment_method
        });
        showSnackbar(response.data.message);
    } catch (error) {
        console.error('Failed to add payment method:', error);
    } finally {
        fetchPMs();
        destroyCard();
        formData.name = null;
        loading.value = false;
        setupIntent.value = null;
        dialogSwitch.value = false;
    }
};
const setPmDefault = (card) => {
    if (confirm(`Are you sure to set card "**** ${card.last_four}" as default?`)) {
        send('post', `payments/set-method-default/${card.id}`)
            .then((response) => {
                if (response.data.success) {
                    fetchPMs();
                    showSnackbar(response.data.message);
                }
            })
            .catch((error) => {
                if (error.response.data.message) {
                    showSnackbar(error.response.data.message, 'error')
                } else {
                    showSnackbar(`"${card.last_four}" card could not set as default`, 'error')
                }
            })
    }
}
const deletePm = (card) => {
    if (confirm(`Are you sure to delete card "**** ${card.last_four}"?`)) {
        send('delete', `payments/delete-method/${card.id}`)
            .then((response) => {
                if (response.data.success) {
                    cards.value.map((item, index) => {
                        if (item.id === card.id) {
                            cards.value.splice(index, 1);
                        }
                    });
                    showSnackbar(response.data.message);
                }
            })
            .catch((error) => {
                if (error.response.data.message) {
                    showSnackbar(error.response.data.message, 'error')
                } else {
                    showSnackbar(`"${card.last_four}" card could not be deleted`, 'error')
                }
            })
    }
}
</script>

<style scoped>
.trust-section {
    background: rgba(227, 242, 253, 0.5);
    border: 1px solid #BBDEFB;
}
</style>
